import { Box, Modal, Select, Text, TextInput, createStyles } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ACTIVITY_TYPES } from "../../constants/activityLibrary";
import { createActivityLibraryItem, editActivityLibraryItem } from "../../services/activityLibrary";

// Locks the modal chrome to the mockup's .modal.sm exactly: 440px width,
// radius 12, header/body/footer padding of 16-22 / 18-22 / 16-22, and a
// bordered footer strip (the mockup's .modal-foot) instead of an in-body row.
const useStyles = createStyles(() => ({
  mymodal: {
    "& .mantine-Modal-modal": {
      borderRadius: 12,
      padding: "0 !important",
    },
    "& .mantine-Modal-header": {
      padding: "16px 22px",
      margin: 0,
      borderBottom: "1px solid var(--app-border)",
    },
    "& .mantine-Modal-title": {
      fontFamily: "Inter",
      fontSize: 16,
      fontWeight: 600,
    },
    "& .mantine-Modal-body": {
      padding: "18px 22px",
    },
  },
  foot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 22px",
    borderTop: "1px solid var(--app-border)",
    margin: "18px -22px -18px",
  },
}));

const fieldStyles = {
  label: { fontFamily: "Inter", fontSize: 14, fontWeight: 500, marginBottom: 8, color: "var(--app-text)" },
  input: {
    height: 44,
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 14,
    boxShadow: "0px 1px 2px 0px rgba(16,24,40,.05)",
    borderColor: "var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
  },
  // Matches the mockup's custom .dd-menu/.dd-opt dropdown (amber-highlighted selected item).
  dropdown: {
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(16,24,40,.16)",
    padding: 6,
    background: "var(--app-surface)",
  },
  item: {
    borderRadius: 8,
    fontSize: 14,
    fontFamily: "Inter",
    padding: "10px 12px",
    color: "var(--app-text)",
    "&[data-selected]": { backgroundColor: "var(--app-tint-amber) !important", color: "var(--app-ink-amber) !important", fontWeight: 600 },
    "&[data-hovered]": { backgroundColor: "var(--app-surface-2)" },
  },
};

const ADD_CUSTOM_TYPE = "+ Add custom type";
// The ticket's per-activity field list is just Name / Type / Order / Active —
// Section isn't a required input, so this form doesn't ask for one (matching
// the mockup, whose flat item list has no section field at all). New items
// still need a real section for the picker's grouped display, so they land
// in a dedicated "Other" catch-all under Common Shift Activities — distinct
// from the ticket's named groups (Meals, Medication, etc.) rather than a
// fabricated one, and it renders as its own normal section in the picker.
const DEFAULT_LAYER = "common_shift";
const DEFAULT_SECTION = "Other";

// data = existing activity (edit mode). Only Activity title + Activity Type
// are ever editable — matches the mockup's "Add/Edit Quick Note Item" modal
// exactly: no Section/Layer fields, and Activity Type offers an explicit
// "+ Add custom type" option that reveals a second text input, rather than
// an inline creatable-search dropdown.
const ActivityLibraryModal = ({ opened, close, data }) => {
  const { classes } = useStyles();
  const { activities } = useSelector((state) => state.activityLibrary);
  const edit = !!data?.id;

  const [typeOptions, setTypeOptions] = useState([]);
  const [customType, setCustomType] = useState(false);
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      name: data?.name || "",
      type: data?.type || "",
    },
    validate: {
      name: (value) => (value ? null : "Activity title is required"),
      type: (value) => (value ? null : "Activity Type is required"),
    },
  });

  useEffect(() => {
    if (opened) {
      setCustomType(false);
      form.setValues({ name: data?.name || "", type: data?.type || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, data?.id]);

  useEffect(() => {
    const usedTypes = activities?.map((a) => a.type).filter(Boolean) || [];
    setTypeOptions([...new Set([...ACTIVITY_TYPES, ...usedTypes])]);
  }, [activities]);

  const onTypeChange = (value) => {
    if (value === ADD_CUSTOM_TYPE) {
      setCustomType(true);
      form.setFieldValue("type", "");
    } else {
      setCustomType(false);
      form.setFieldValue("type", value);
    }
  };

  const closeHandler = () => {
    form.reset();
    close();
  };

  const onSave = () => {
    const valid = form.validate();
    if (valid.hasErrors) return;

    const name = form.values.name.trim();
    const type = form.values.type.trim();

    setSaving(true);
    const done = () => setSaving(false) || closeHandler();

    if (edit) {
      editActivityLibraryItem({ id: data.id, layer: data.layer, section: data.section, name, type, order: data.order ?? 0, active: data.active ?? true }, done);
    } else {
      // New items should show up at the top of the list (ticket requirement),
      // and the table sorts ascending by order, so go below the current
      // lowest order instead of above the highest.
      const siblingOrders = (activities || []).map((a) => a.order ?? 0);
      const order = siblingOrders.length ? Math.min(...siblingOrders) - 1 : 0;
      createActivityLibraryItem({ layer: DEFAULT_LAYER, section: DEFAULT_SECTION, name, type, order, active: true }, done);
    }
  };

  return (
    <Modal
      className={classes.mymodal}
      opened={opened}
      onClose={closeHandler}
      size={440}
      radius={12}
      shadow="md"
      zIndex={1000}
      overlayProps={{ blur: 3 }}
      centered
      title={edit ? "Edit Quick Note Item" : "Add Quick Note Item"}
    >
      <Text fz={13} c="dimmed" mb={14} ff="Inter">
        Items added here appear under <b>Common Shift Activities</b> in the Quick Notes picker.
      </Text>

      <TextInput label="Activity title" mb={16} styles={fieldStyles} placeholder="e.g. Ate all meals well" {...form.getInputProps("name")} />

      <Select
        label="Activity Type"
        mb={customType ? 16 : 0}
        styles={fieldStyles}
        placeholder="Select an activity type"
        searchable
        data={[...typeOptions, ADD_CUSTOM_TYPE]}
        value={customType ? ADD_CUSTOM_TYPE : form.values.type}
        onChange={onTypeChange}
        error={form.errors.type}
      />

      {customType && (
        <TextInput
          styles={fieldStyles}
          placeholder="New activity type name"
          value={form.values.type}
          onChange={(e) => form.setFieldValue("type", e.currentTarget.value)}
        />
      )}

      <Box className={classes.foot}>
        <Box
          component="button"
          onClick={closeHandler}
          sx={{
            border: "1px solid var(--app-border)", background: "var(--app-surface)", borderRadius: 8, padding: "11px 18px",
            fontFamily: "Inter", fontWeight: 600, fontSize: 14, cursor: "pointer", color: "var(--app-text)",
            "&:hover": { background: "var(--app-surface-2)" },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={onSave}
          disabled={saving}
          sx={{
            border: "none", background: "var(--app-primary)", borderRadius: 8, padding: "11px 18px",
            fontFamily: "Inter", fontWeight: 600, fontSize: 14, color: "var(--app-on-primary)",
            cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
            "&:hover": saving ? {} : { filter: "brightness(.96)" },
          }}
        >
          {edit ? "Save" : "Add"}
        </Box>
      </Box>
    </Modal>
  );
};

export default ActivityLibraryModal;
